// Mirror of the Pydantic models in app/ — update together.

export type Effect = "solid" | "off";

export interface LedState {
  color: string;
  luminance: number;
  effect: Effect;
}

export interface OverrideState {
  color: string;
  luminance: number;
  effect: Effect;
  expires_ts: number | null;
  paused: boolean;
}

export interface AppliedState {
  color: string | null;
  luminance: number | null;
  effect: string | null;
  matched_event_id: string | null;
  matched_rule_name: string | null;
  override: OverrideState | null;
  last_webhook_ts: number | null;
  meross_ready: boolean;
}

// /api/status payload (read-only).
export interface StatusResponse {
  server: {
    version: string;
    uptime_s: number;
    dev_mode: boolean;
    time_ts: number;
    time_iso: string;
    timezone: string;
  };
  meross: { ready: boolean; circuit_open: boolean };
  led: (LedState & { applied_ts: number; seconds_ago: number }) | null;
  override: OverrideState | null;
  last_webhook: {
    received_ts: number;
    reason: string;
    tz: string | null;
    window_start: number | null;
    window_end: number | null;
    seconds_ago: number;
    is_stale: boolean;
  } | null;
  matched_event: {
    rule: string;
    event_id: string | null;
    event_title: string | null;
    event_start: string | null;
    event_end: string | null;
  } | null;
  evaluator: {
    last_tick_ts: number | null;
    last_result_kind: string | null;
    last_rule_name: string | null;
    last_event_id: string | null;
    last_gate: string | null;
    interval_seconds: number;
  };
  events: EventSummary[];
}

export interface EventSummary {
  id: string;
  title: string | null;
  start: string | null;
  end: string | null;
  calendar_id: string | null;
  calendar_name: string | null;
  is_primary_calendar: boolean;
  all_day: boolean;
  event_type: string;
  visibility: string | null;
  color_id: string | null;
  attendee_count: number;
  my_response: string | null;
  is_organizer: boolean;
  status: string | null;
  transparency: string | null;
  recurring: boolean;
  matched_rule: string;
  is_matched: boolean;
}

// Rules / presets.
export type ActionKind = "preset" | "skip" | "do_nothing";

export type Weekday =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

export const WEEKDAYS: Weekday[] = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

export interface RulePredicate {
  title_regex?: string | null;
  title_regex_flags?: string | null;
  calendar_name_regex?: string | null;
  calendar_name_regex_flags?: string | null;
  transparency?: "opaque" | "transparent" | null;
  all_day?: boolean | null;
  my_response?: string[] | null;
  visibility?: string[] | null;
  is_primary_calendar?: boolean | null;
  organizer?: boolean | null;
  event_type?: string[] | null;
  status?: "confirmed" | "cancelled" | "tentative" | null;
  days_of_week?: Weekday[] | null;
  start_time?: string | null;
  end_time?: string | null;
  // Context predicates.
  no_events?: boolean | null;
  no_effective_events?: boolean | null;
  working_hours?: boolean | null;
  not_working_day?: boolean | null;
}

export interface FallbackRule {
  enabled: boolean;
  action_kind: "preset" | "do_nothing";
  preset_id: number | null;
  preset: Preset | null;
  updated_ts: number;
}

export interface WorkingHoursRow {
  day: Weekday;
  enabled: boolean;
  start_time: string;
  end_time: string;
  updated_ts: number;
}

export interface WorkingHoursEntryInput {
  day: Weekday;
  enabled: boolean;
  start_time: string;
  end_time: string;
}

export interface BackupFile {
  version: 1 | 2 | 3;
  exported_at: string;
  presets: Array<{ name: string; color: string; luminance: number; effect: Effect }>;
  rules: Array<{
    name: string;
    priority: number;
    enabled: boolean;
    predicate: RulePredicate;
    action_kind: ActionKind;
    preset_name: string | null;
  }>;
  working_hours: WorkingHoursEntryInput[];
  fallback?: {
    enabled: boolean;
    action_kind: "preset" | "do_nothing";
    preset_name: string | null;
  } | null;
}

export interface Preset {
  id: number;
  name: string;
  color: string;
  luminance: number;
  effect: Effect;
  created_ts: number;
  updated_ts: number;
}

export interface Rule {
  id: number;
  priority: number;
  name: string;
  enabled: boolean;
  predicate: RulePredicate;
  action_kind: ActionKind;
  preset_id: number | null;
  preset: Preset | null;
  created_ts: number;
  updated_ts: number;
}

export interface LogEvent {
  ts: number;
  kind: string;        // "led.apply" | "led.skip" | "evaluator.tick" | "evaluator.gate" | "webhook.ingest" | "override.set" | "override.clear" | "pause.start" | "pause.stop" | "led.error"
  message: string;
  detail: Record<string, unknown>;
}

// Auth.
export interface MeResponse {
  authenticated: boolean;
  username?: string;
  csrf_token?: string;
  remember?: boolean;
  expires_ts?: number;
}
